/**
 * The tab's title: the app's name with the unread count, "(3) Neosocial",
 * and the flash that alternates it with a message until the window gets the
 * focus (pokes, DMs and calls on the web).
 */

let base: string | null = null;
let count = 0;
let flash: {
  text: string;
  /** The name when the flash began (the title itself is the flash text meanwhile). */
  name: string;
  on: boolean;
  timer: ReturnType<typeof setInterval>;
} | null = null;

const COUNT_PREFIX = /^\(\d+\+?\) /;

/** "(3) Neosocial"; over 99 shows "99+". */
export function titleWithCount(name: string, n: number): string {
  if (n <= 0) return name;
  return `(${n > 99 ? '99+' : n}) ${name}`;
}

function nameNow(): string {
  if (base !== null) return base;
  if (flash) return flash.name;
  return typeof document === 'undefined' ? '' : document.title.replace(COUNT_PREFIX, '');
}

/** The title without a flash: the name (set by the shell, else the page's own) and the count. */
export function composedTitle(): string {
  return titleWithCount(nameNow(), count);
}

function render(): void {
  if (typeof document === 'undefined') return;
  if (flash) return; // the flash puts the composed title back on its "off" beat
  document.title = composedTitle();
}

/** The app's name (the console's texts.app_name). */
export function setTitleName(name: string): void {
  base = name;
  render();
}

/** The unread count (0 hides it). */
export function setTitleCount(n: number): void {
  count = Math.max(0, Math.floor(n));
  render();
}

function stopOnFocus(): void {
  stopTitleFlash();
}

/** The title alternates with `text` every second until the window gets the focus. */
export function flashTitle(text: string): void {
  if (typeof document === 'undefined' || document.hasFocus()) return;
  const name = nameNow();
  stopTitleFlash();
  const f = {
    text,
    name,
    on: true,
    timer: setInterval(() => {
      f.on = !f.on;
      document.title = f.on ? f.text : composedTitle();
    }, 1000),
  };
  flash = f;
  document.title = text;
  addEventListener('focus', stopOnFocus);
}

export function stopTitleFlash(): void {
  if (!flash) return;
  const name = flash.name;
  clearInterval(flash.timer);
  flash = null;
  removeEventListener('focus', stopOnFocus);
  if (typeof document !== 'undefined') document.title = titleWithCount(base ?? name, count);
}

/** Tests start from a clean title. */
export function resetTitle(): void {
  stopTitleFlash();
  base = null;
  count = 0;
}
